import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  Loader2,
  PlaneTakeoff,
  Check,
  CreditCard,
  RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { stripePromise } from '@/lib/stripe';
import { getApiUrl } from '@/lib/apiUrls';
import { mergeSupabaseEdgeHeaders } from '@/lib/reserveActAs';
import { formatStripeErrorMessage } from '@/lib/stripeErrors';
import {
  changeFlightNeedsRefundableTarget,
  isRefundableFareBrand,
  NON_REFUNDABLE_CHANGE_BLOCKED_MESSAGE,
} from '@/lib/fareBrandRefundability';
import { tripHasAirlineConfirmation } from '@/utils/changeFlightEligibility';
import { fmtCurrency } from '@/utils/money';
import { purchasedFareBrand } from '@/utils/cabinClass';
import { insuranceRepriceNote } from '@/utils/insuranceRepriceNote';

function formatDuration(isoDuration) {
  if (isoDuration == null) return '';
  if (typeof isoDuration === 'number') {
    const h = Math.floor(isoDuration / 60);
    const m = isoDuration % 60;
    return `${h}h ${m}m`;
  }
  const matches = String(isoDuration).match(/PT(\d+H)?(\d+M)?/);
  if (!matches) return '';
  const hours = matches[1] ? parseInt(matches[1].slice(0, -1)) : 0;
  const minutes = matches[2] ? parseInt(matches[2].slice(0, -1)) : 0;
  return `${hours}h ${minutes}m`;
}

function formatTime(isoString) {
  if (!isoString) return '';
  return format(parseISO(isoString), 'h:mm a');
}

function formatDate(isoString) {
  if (!isoString) return '';
  return format(parseISO(isoString), 'EEE, MMM d');
}

function FlightSummaryCard({ flightData, label, bookingOption, searchParams }) {
  const slices = flightData?.slices || [];
  const cabinClass = searchParams?.cabin_class || 'economy';

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
      <p className="text-white/40 text-xs uppercase tracking-wider mb-4">{label}</p>
      {slices.map((slice, idx) => {
        const segment = slice.segments?.[0];
        const lastSegment = slice.segments?.[slice.segments?.length - 1];
        if (!segment) return null;
        const marketingCarrier = segment.marketing_carrier;
        const stops = (slice.segments?.length || 1) - 1;
        const fareOption = idx === 0
          ? (bookingOption?.departing || bookingOption?.together)
          : (bookingOption?.returning || bookingOption?.together);
        const cabinDisplay = fareOption?.option_title || segment.passengers_cabin_class_marketing_name || cabinClass;

        return (
          <div key={idx} className="mb-6 last:mb-0">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/60 text-xs font-normal uppercase">
                {idx === 0 ? 'Departing' : 'Return'} • {formatDate(segment.departing_at)}
              </span>
              {stops > 0 && (
                <span className="text-brand-400/80 text-[10px] uppercase">{stops} stop{stops > 1 ? 's' : ''}</span>
              )}
            </div>
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex justify-center overflow-hidden">
                  {marketingCarrier?.logo_symbol_url ? (
                    <img src={marketingCarrier.logo_symbol_url} alt="" className="w-full h-full object-contain" />
                  ) : (
                    <PlaneTakeoff className="w-5 h-5 text-white/40" />
                  )}
                </div>
                <div>
                  <div className="text-white text-sm font-medium">
                    {formatTime(segment.departing_at)} – {formatTime(lastSegment?.arriving_at)}
                  </div>
                  <div className="text-white/40 text-xs">
                    {marketingCarrier?.name}
                    {segment.marketing_carrier_flight_number && ` ${segment.marketing_carrier_flight_number}`}
                  </div>
                  {cabinDisplay && (
                    <div className="text-white/30 text-xs capitalize">{cabinDisplay}</div>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-white/50 text-xs uppercase">
                  {segment.origin?.iata_code}–{lastSegment?.destination?.iata_code}
                </div>
                <div className="text-white/60 text-xs">{formatDuration(slice.duration)}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontWeight: '500',
      '::placeholder': { color: 'rgba(255, 255, 255, 0.4)' },
    },
    invalid: { color: '#f87171', iconColor: '#f87171' },
  },
  hidePostalCode: false,
};

/** Change-flight refusals that mean "the price moved: fetch it again". */
const REPRICE_CODES = new Set(['price_changed', 'quote_expired', 'payment_required', 'insurance_price_changed']);
/** Preview refusals that block the change (nothing can be charged). */
const BLOCKING_PREVIEW_CODES = new Set(['price_unavailable', 'same_flight', 'quote_unavailable']);

function ChangeFlightCheckoutForm({ dueTodayCents, changePayload, onSuccess, onError, onReprice }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || dueTodayCents <= 0 || !changePayload) return;
    setIsProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Please sign in to continue');

      const res = await fetch(getApiUrl('bookings/change-flight'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        }),
        body: JSON.stringify({
          create_payment_only: true,
          due_today_cents: dueTodayCents,
          ...changePayload,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok && REPRICE_CODES.has(data?.code)) {
        toast.error(data.error);
        onReprice?.();
        return;
      }
      if (!res.ok || !data.client_secret) throw new Error(formatStripeErrorMessage(data) || data.error || 'Failed to create payment');

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) throw new Error('Card input not found');

      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(data.client_secret, {
        payment_method: { card: cardElement },
      });
      if (confirmError) throw new Error(formatStripeErrorMessage(confirmError) || confirmError.message);
      // The card is authorized ('requires_capture'); the server captures it
      // only once it has checked the payment against its priced quote.
      if (paymentIntent?.status !== 'requires_capture' && paymentIntent?.status !== 'succeeded') {
        throw new Error('Payment did not complete');
      }

      // Only the payment is sent: the server saves the change it priced.
      const confirmRes = await fetch(getApiUrl('bookings/change-flight/confirm'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        }),
        body: JSON.stringify({ payment_intent_id: paymentIntent.id }),
      });
      const confirmData = await confirmRes.json().catch(() => ({}));
      if (!confirmRes.ok) throw new Error(confirmData.error || 'Failed to confirm change');

      toast.success('Flight changed successfully!');
      onSuccess?.();
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
      onError?.(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
        <p className="text-white/80 text-sm mb-3">Card details</p>
        <CardElement options={CARD_ELEMENT_OPTIONS} className="p-3 rounded-xl border border-white/10 bg-white/5" />
      </div>
      <Button
        type="submit"
        disabled={!stripe || !elements || isProcessing}
        className="w-full bg-white text-black hover:bg-white/90 h-14 rounded-full font-medium"
      >
        {isProcessing ? (
          <>
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5 mr-2" />
            Pay ${fmtCurrency(dueTodayCents)} & change flight
          </>
        )}
      </Button>
    </form>
  );
}

export default function ChangeFlightReview() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    selectedFlight,
    returnFlight,
    searchParams,
    bookingOption,
    changeFlightTrip,
  } = location.state || {};

  const [preview, setPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  /** Bumped to fetch the preview again (the travel insurance price moved under us). */
  const [previewNonce, setPreviewNonce] = useState(0);
  const [paymentState, setPaymentState] = useState(null); // { client_secret, due_today_cents } or null
  /** The server couldn't price this flight (or it's the current one): nothing can be charged. */
  const [blockingError, setBlockingError] = useState(null);

  const isOneWay = !returnFlight && (!searchParams?.slices || searchParams.slices.length === 1);

  const newFlightData = useMemo(() => {
    if (!selectedFlight) return null;
    const outSlices = selectedFlight?.slices || [];
    const retSlices = returnFlight?.slices || [];
    const slices = retSlices.length > 0 ? [...outSlices, ...retSlices] : outSlices;
    /**
     * Pin total_amount/base_amount to the chosen fare brand's price.
     *
     * `selectedFlight` is the SerpAPI/Google-Flights search result — its
     * `total_amount`/`base_amount` carry the aggregator's display price, which can
     * differ from the actual booking price of the fare brand the customer picked
     * (Basic vs. Choice Preferred vs. Anytime, etc.). 13Q84U's regression had
     * `selectedFlight.total_amount = "230"` while the picked fare brand
     * (`bookingOption.together.price = 185`) was a downgrade to Basic, leaving the
     * JSONB internally inconsistent and breaking the Cost Breakdown card.
     *
     * Falls through to whatever `selectedFlight` carried only when no
     * bookingOption price is available (legacy non-brand-aware change flow).
     */
    const fareBrandBase = (() => {
      const together = parseFloat(bookingOption?.together?.price ?? 0);
      if (together > 0) return together;
      const dep = parseFloat(bookingOption?.departing?.price ?? 0);
      const ret = parseFloat(bookingOption?.returning?.price ?? 0);
      if (dep > 0 || ret > 0) return dep + ret;
      return 0;
    })();
    const fareBrandOverride = fareBrandBase > 0
      ? { total_amount: String(fareBrandBase), base_amount: String(fareBrandBase) }
      : {};
    return {
      ...selectedFlight,
      ...fareBrandOverride,
      slices,
      _searchParams: searchParams,
      bookingOption,
      _returnFlight: returnFlight,
    };
  }, [selectedFlight, returnFlight, searchParams, bookingOption]);

  const { newBaseFareCents, newServiceFeeCents, newTotalCents } = useMemo(() => {
    const main = bookingOption?.together || bookingOption?.departing || bookingOption?.returning;
    let base = 0;
    if (main?.price) {
      base = Math.round(parseFloat(main.price) * 100);
    } else if (selectedFlight && returnFlight) {
      const outPrice = parseFloat(selectedFlight.price || selectedFlight.total_amount || 0);
      const retPrice = parseFloat(returnFlight.price || returnFlight.total_amount || 0);
      base = Math.round((outPrice + retPrice) * 100);
    } else if (selectedFlight) {
      base = Math.round(parseFloat(selectedFlight.price || selectedFlight.total_amount || 0) * 100);
    }
    const service = Math.round(base * 0.11);
    return {
      newBaseFareCents: base,
      newServiceFeeCents: service,
      newTotalCents: base + service,
    };
  }, [bookingOption, selectedFlight, returnFlight]);

  /**
   * Frontend mirror of the Edge Function's `getOriginalBaseFareCents`. Same
   * priority order — see supabase/functions/change-flight-preview/index.ts
   * for the rationale (NJM4JY canary, raw-vs-raw comparison requirement).
   *
   * This is only the local fallback when the preview API call fails (e.g.
   * admin act-as 404). When `preview` is set, `dueTodayCents` and
   * `flightCreditCents` come straight from the API and this value is purely
   * cosmetic for the "Original base fare" label.
   */
  const originalBaseFareCents = useMemo(() => {
    if (!changeFlightTrip?.length) return 0;
    const main = changeFlightTrip[0];
    const fd = main?.flightData || main?.flight_data || {};
    const ps = fd?.pricing_snapshot;
    const actualCost = parseFloat(fd?._actual_cost ?? fd?.actual_cost ?? 0);
    if (actualCost > 0) return Math.round(actualCost * 100);
    if (ps?.original_base_fare != null && parseFloat(ps.original_base_fare) > 0) {
      const svc = parseFloat(ps?.service_fee || main?.service_fee || fd?.service_fee || 0);
      const hold = parseFloat(ps?.hold_fee || main?.hold_fee || fd?.hold_fee || 0);
      const gt = parseFloat(ps?.grand_total || main?.grand_total || main?.grandTotal || fd?.grand_total || 0);
      let base = parseFloat(ps.original_base_fare);
      if (hold > 0 && gt > 0) {
        const derivedBase = Math.max(0, gt - svc - hold);
        if (base > derivedBase + 1) base = derivedBase;
      }
      return Math.round(base * 100);
    }
    const optPrice = parseFloat(fd?.bookingOption?.together?.price ?? 0);
    if (optPrice > 0) return Math.round(optPrice * 100);
    const fdTotal = parseFloat(fd?.total_amount || fd?.totalAmount || 0);
    if (fdTotal > 0) return Math.round(fdTotal * 100);
    const fdBase = parseFloat(fd?.base_amount ?? 0);
    if (fdBase > 0) return Math.round(fdBase * 100);
    const mainTotal = parseFloat(main?.total_amount || main?.totalAmount || 0);
    if (mainTotal > 0) return Math.round(mainTotal * 100);
    const gt = parseFloat(main?.grand_total || main?.grandTotal || fd?.grand_total || 0);
    const svc = parseFloat(ps?.service_fee || main?.service_fee || fd?.service_fee || 0);
    if (gt > 0 && svc >= 0) return Math.round(Math.max(0, gt - svc) * 100);
    return 0;
  }, [changeFlightTrip]);

  // Base fare difference vs the basis (Actual Cost first, then original base —
  // see originalBaseFareCents above).
  const baseFareDiffCents = newBaseFareCents - originalBaseFareCents;
  const existingCreditCents = changeFlightTrip?.[0]?.flightData?._change_flight_credit_cents ?? changeFlightTrip?.[0]?.flight_data?._change_flight_credit_cents ?? 0;

  // Flight credits are NEVER surfaced or accrued in the change flow. At or below
  // basis the change is free ($0 today); above basis we bill the difference
  // upfront. Any credit owed back to the customer is presented/handled manually
  // by the admin at booking time, so we don't compute or store one here.
  const localFareDueCents = baseFareDiffCents > 0 ? Math.round(baseFareDiffCents * 1.11) : 0;
  const dueTodayCents = preview?.due_today_cents ?? localFareDueCents;
  /** The fare difference this page shows (without insurance): the server
   *  never charges more than this, and re-prices the page instead. */
  const shownFareDueCents = preview?.travel_due_today_cents ?? preview?.due_today_cents ?? localFareDueCents;
  /** The server's quote for this change (change-flight-preview). */
  const quoteId = preview?.quote_id || null;
  const flightCreditCents = 0;
  const newCreditBalanceCents = existingCreditCents; // preserved, never auto-changed
  /** Signed base-fare delta (new − basis); drives upgrade / no-change labels in the summary row */
  const priceDiffCents = baseFareDiffCents;
  const displayNewBaseFareCents = newBaseFareCents;
  const fd0 = changeFlightTrip?.[0]?.flightData || changeFlightTrip?.[0]?.flight_data;
  const isDeferaZero = fd0?._payment_method === 'defera_zero' || fd0?.payment_method === 'defera_zero';
  const isDeferaCapital = fd0?._payment_method === 'defera_capital' || fd0?.payment_method === 'defera_capital';
  const isStripeInstallmentTrip = isDeferaZero || isDeferaCapital;

  /**
   * Defera Zero must never carry a non-refundable fare — see
   * src/lib/fareBrandRefundability.js / supabase/functions/_shared/fare-brand-refundability.ts.
   * The Edge Function enforces this, but we surface the block here so the customer
   * gets an immediate, intelligible error instead of a generic 400 toast at submit.
   */
  const targetPaymentMethod = isDeferaZero ? 'defera_zero' : (isDeferaCapital ? 'defera_capital' : null);
  // The Defera Zero "must stay refundable" guard only matters once the ticket is
  // actually booked (airline PNR exists). Before that, the customer is still
  // choosing their flight and may pick any fare brand, incl. Basic Economy.
  const tripBooked = tripHasAirlineConfirmation(changeFlightTrip);
  const blockedAsNonRefundable =
    tripBooked &&
    changeFlightNeedsRefundableTarget(targetPaymentMethod) &&
    !isRefundableFareBrand(bookingOption);

  useEffect(() => {
    if (!changeFlightTrip?.length || !newFlightData) {
      if (!changeFlightTrip?.length) {
        toast.error('Missing trip information');
        navigate('/trips');
      }
      setIsLoadingPreview(false);
      return;
    }

    const fetchPreview = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/home?auth_mode=login');
          return;
        }
        const res = await fetch(getApiUrl('bookings/change-flight/preview'), {
          method: 'POST',
          headers: mergeSupabaseEdgeHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          }),
          body: JSON.stringify({
            booking_id: changeFlightTrip[0].id,
            new_flight_data: newFlightData,
            new_total_cents: newTotalCents,
            new_base_fare_cents: newBaseFareCents,
            new_service_fee_cents: newServiceFeeCents,
          }),
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setPreview(data);
          setBlockingError(null);
        } else if (BLOCKING_PREVIEW_CODES.has(data?.code)) {
          // The server prices the change; when it can't, there is no price to pay.
          setPreview(null);
          setBlockingError(data.error);
        } else {
          // The preview is an enhancement only — the page falls back to the
          // deterministic local pricing (see dueTodayCents / baseFareDiffCents),
          // and the authoritative charge is computed server-side at submit. A
          // preview miss (e.g. the spurious "Booking not found" 404 on initial
          // load from admin act-as / a transient session) must NOT alarm the
          // customer, so we log and silently use the local fallback.
          console.warn('change-flight preview unavailable, using local fallback:', data?.error);
        }
      } catch (err) {
        console.warn('change-flight preview request failed, using local fallback:', err?.message);
      } finally {
        setIsLoadingPreview(false);
      }
    };

    fetchPreview();
  }, [changeFlightTrip, newFlightData, newTotalCents, newBaseFareCents, newServiceFeeCents, navigate, previewNonce]);

  /** Fetch the price again (it moved, or the quote expired). */
  const reprice = () => {
    setPreview(null);
    setIsLoadingPreview(true);
    setPreviewNonce((n) => n + 1);
  };

  /**
   * Confirm the change for an installment trip (Defera Zero / DC-Stripe / DC-NMI).
   *
   * For an upgrade (dueTodayCents > 0) the backend charges the delta upfront on
   * the trip's processor of record (Stripe saved-card off-session for DZ +
   * DC-Stripe, NMI vault MIT for DC-NMI). For a downgrade or wash the backend
   * just records the change. In neither case does it modify the installment
   * schedule — that's the whole point of the upfront-charge model: the
   * customer's existing payment plan stays untouched.
   *
   * `client_request_id` is folded into the upstream Stripe Idempotency-Key
   * and NMI orderid so a retried submit (slow network, double click) can't
   * double-charge the upgrade delta.
   */
  const handleConfirmInstallmentTrip = async () => {
    if (!changeFlightTrip?.length || !newFlightData) return;
    if (blockedAsNonRefundable) {
      toast.error(NON_REFUNDABLE_CHANGE_BLOCKED_MESSAGE);
      return;
    }
    setIsLoadingPreview(true);
    /** A re-price keeps the spinner up until the new price arrives. */
    let repricing = false;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const clientRequestId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `cf-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      const res = await fetch(getApiUrl('bookings/change-flight'), {
        method: 'POST',
        headers: mergeSupabaseEdgeHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        }),
        body: JSON.stringify({
          booking_id: changeFlightTrip[0].id,
          flight_data: { ...newFlightData, _change_flight_credit_cents: newCreditBalanceCents },
          new_total_cents: newBaseFareCents,
          new_base_fare_cents: newBaseFareCents,
          new_service_fee_cents: Math.round(newBaseFareCents * 0.11),
          is_defera_zero: true,
          client_request_id: clientRequestId,
          quote_id: quoteId,
          // The fare difference this page showed; the server never charges more.
          expected_fare_due_cents: shownFareDueCents,
          // The travel insurance increase this page showed; the server refuses a different one.
          expected_insurance_charge_cents: preview?.insurance?.charge_today_cents ?? 0,
        }),
      });
      const data = await res.json();
      if (!res.ok && REPRICE_CODES.has(data?.code)) {
        toast.error(data.error);
        repricing = true;
        reprice();
        return;
      }
      if (!res.ok && BLOCKING_PREVIEW_CODES.has(data?.code)) {
        setBlockingError(data.error);
        return;
      }
      if (!res.ok) throw new Error(formatStripeErrorMessage(data) || data.error || 'Failed to change flight');

      if (dueTodayCents > 0 && data.amount_charged_cents > 0) {
        toast.success(`Flight changed and $${fmtCurrency(data.amount_charged_cents)} charged to your card on file.`);
      } else {
        toast.success('Flight changed.');
      }
      navigate('/trips');
    } catch (err) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      if (!repricing) setIsLoadingPreview(false);
    }
  };

  const handleSuccess = () => {
    navigate('/trips');
  };

  const fmt = (c) => (c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const originalFlightData = changeFlightTrip?.[0]?.flightData || changeFlightTrip?.[0]?.flight_data || {};

  if (!changeFlightTrip?.length) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/60 mb-4">Missing trip information</p>
          <Button onClick={() => navigate('/trips')} variant="outline">
            Back to Trips
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Change Flight | Defera</title>
      </Helmet>
      <div className="min-h-screen bg-black text-white">
        <div className="max-w-3xl mx-auto px-6 md:px-8 lg:px-10 py-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center">
              <RefreshCw className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-light">Change Flight</h1>
              <p className="text-white/65 text-sm">Compare your current itinerary with the new selection</p>
            </div>
          </div>

          {/* Flight comparison */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <FlightSummaryCard
              flightData={originalFlightData}
              label="Original itinerary"
              bookingOption={null}
              searchParams={{ cabin_class: purchasedFareBrand(changeFlightTrip[0]) || 'economy' }}
            />
            <FlightSummaryCard
              flightData={newFlightData}
              label="New itinerary"
              bookingOption={bookingOption}
              searchParams={searchParams}
            />
          </div>

          {/* Due Today & Checkout */}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 mb-8">
            <p className="text-white/40 text-xs uppercase tracking-wider mb-4">Due today</p>
            <p className="text-white text-2xl font-light mb-6">
              {blockingError ? '—' : dueTodayCents <= 0 ? '$0.00' : `$${fmt(dueTodayCents)}`}
            </p>
            {blockingError && (
              <div
                role="alert"
                className="rounded-2xl border border-red-500/40 bg-red-500/10 p-4 mb-4"
              >
                <p className="text-red-200/80 text-sm leading-relaxed">{blockingError}</p>
              </div>
            )}
            {blockedAsNonRefundable && (
              <div
                role="alert"
                className="rounded-2xl border border-red-500/40 bg-red-500/10 p-4 mb-4"
              >
                <p className="text-red-300 text-sm font-normal mb-1">
                  This fare can't be carried on Defera Zero
                </p>
                <p className="text-red-200/80 text-sm leading-relaxed">
                  {NON_REFUNDABLE_CHANGE_BLOCKED_MESSAGE}
                </p>
              </div>
            )}
            {isLoadingPreview ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-white/40" />
              </div>
            ) : blockingError ? null : isStripeInstallmentTrip ? (
              /**
               * Installment trips charge the upgrade delta UPFRONT on the
               * trip's processor of record (Stripe saved card for DZ +
               * DC-Stripe, NMI vault for DC-NMI). The old "No charge
               * today, just adjusts your installments" UX is gone — that
               * path silently rewrote the subscription total and was the
               * source of NJM4JY's $29.95 mis-credit on a Main → Main
               * Plus upgrade. Now: pay the diff now, your schedule
               * doesn't change.
               */
              <div>
                {dueTodayCents > 0 ? (
                  <p className="text-white/85 text-sm mb-4">
                    We'll charge ${fmt(dueTodayCents)} to your card on file. Your installment schedule won't change.
                  </p>
                ) : (
                  <p className="text-white/85 text-sm mb-4">
                    No charge today. Your installment schedule won't change.
                  </p>
                )}
                {insuranceRepriceNote(preview?.insurance, fmt) && (
                  <p className="text-white/65 text-sm mb-4">{insuranceRepriceNote(preview?.insurance, fmt)}</p>
                )}
                <Button
                  onClick={handleConfirmInstallmentTrip}
                  disabled={isLoadingPreview || blockedAsNonRefundable || !!blockingError}
                  className="w-full bg-white text-black hover:bg-white/90 h-14 rounded-full font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {isLoadingPreview ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : dueTodayCents > 0 ? (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Confirm & Pay ${fmt(dueTodayCents)}
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      Confirm change
                    </>
                  )}
                </Button>
              </div>
            ) : dueTodayCents > 0 ? (
              <Elements stripe={stripePromise}>
                <ChangeFlightCheckoutForm
                  dueTodayCents={dueTodayCents}
                  changePayload={(blockedAsNonRefundable || blockingError) ? null : {
                    booking_id: changeFlightTrip[0].id,
                    flight_data: newFlightData,
                    new_total_cents: newBaseFareCents,
                    new_base_fare_cents: newBaseFareCents,
                    new_service_fee_cents: Math.round(newBaseFareCents * 0.11),
                    is_defera_zero: false,
                    quote_id: quoteId,
                    expected_fare_due_cents: shownFareDueCents,
                  }}
                  onSuccess={handleSuccess}
                  onReprice={reprice}
                />
              </Elements>
            ) : (
              <Button
                disabled={blockedAsNonRefundable || !!blockingError}
                onClick={async () => {
                  try {
                    const { data: { session } } = await supabase.auth.getSession();
                    if (!session) throw new Error('Not authenticated');
                    const res = await fetch(getApiUrl('bookings/change-flight'), {
                      method: 'POST',
                      headers: mergeSupabaseEdgeHeaders({
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${session.access_token}`,
                      }),
                      body: JSON.stringify({
                        booking_id: changeFlightTrip[0].id,
                        flight_data: { ...newFlightData, _change_flight_credit_cents: newCreditBalanceCents },
                        new_total_cents: newBaseFareCents,
                        new_base_fare_cents: newBaseFareCents,
                        new_service_fee_cents: Math.round(newBaseFareCents * 0.11),
                        is_defera_zero: false,
                        due_today_cents: 0,
                        flight_credit_cents: flightCreditCents,
                        quote_id: quoteId,
                        expected_fare_due_cents: 0,
                      }),
                    });
                    const data = await res.json();
                    if (!res.ok && REPRICE_CODES.has(data?.code)) {
                      toast.error(data.error);
                      reprice();
                      return;
                    }
                    if (!res.ok && BLOCKING_PREVIEW_CODES.has(data?.code)) {
                      setBlockingError(data.error);
                      return;
                    }
                    if (!res.ok) throw new Error(data.error || 'Failed to change flight');
                    toast.success('Flight changed successfully!');
                    navigate('/trips');
                  } catch (err) {
                    toast.error(err.message || 'Something went wrong');
                  }
                }}
                className="w-full bg-white text-black hover:bg-white/90 h-14 rounded-full font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Check className="w-5 h-5 mr-2" />
                Confirm change
              </Button>
            )}
          </div>

          <Button
            onClick={() => navigate(-1)}
            variant="outline"
            className="border border-white/20 bg-white/5 text-white hover:bg-white/10 rounded-full"
          >
            Cancel
          </Button>
        </div>
      </div>
    </>
  );
}
